import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useLazyQuery } from "@apollo/client";
import { CircularProgress, Grid } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { GET_USER_CONTACT_DETAILS } from "../../../graphql/queries";
import AccountSettingsReadOnly from "../AccountSettingsReadOnly";
import DraggableModal from "../DraggableModal";
import DraggableModalHead from "../DraggableModal/DraggableModalHead";
import DraggableModalBody from "../DraggableModal/DraggableModalBody";
import { useMessagingContext } from "../../../hooks/Messaging";
import { useHistory } from "react-router-dom";

import PageRoute from "../../../constant/pageRouteDictionary.json";
import { useMutation } from "@apollo/client";
import { CREATE_CONVERSATION as CREATE_DIRECT_MESSAGE_CONVERSATION } from "../../../graphql/mutations/DirectMessageContext";


const useStyles = makeStyles(theme => ({
  modal: {
    width: "1200px",
    [theme.breakpoints.down("md")]: {
      width: "90%",
    },
  },
  body: {
    padding: 0,
    minHeight: "75vh",
  },
  loading: {
    margin: "50px 0",
  },
}));

const UserContactInfoModal = ({ open, handleClose, username }) => {
  const classes = useStyles();
  const intl = useIntl();
  const [userDetails, setUserDetails] = useState({});

  const { firstname, lastname } = userDetails;
  const history = useHistory();
  const [createConversation] = useMutation(CREATE_DIRECT_MESSAGE_CONVERSATION);

  const { 
    addConversation,
    pendingConversations,
    addPendingConversation,
    conversations } = useMessagingContext();

  const [getUserContactDetails, { data, loading }] = useLazyQuery(
    GET_USER_CONTACT_DETAILS
  );

  useEffect(() => {
    if (open && username) {
      getUserContactDetails({ variables: { username } });
    }
  }, [open, username]);

  useEffect(() => {
    if (
      data &&
      data.userAccountContext &&
      data.userAccountContext.getUserContactDetails
    ) {
      setUserDetails(data.userAccountContext.getUserContactDetails);
    }
  }, [data]);

  const contactInfoText = intl.formatMessage({
    id: "account.contactInformation",
  });

  const headerText = () => {
    let title = "";

    if (firstname) {
      title = `${firstname}`;
      if (lastname) {
        title = `${firstname} ${lastname}`;
      }
      title = `${title} - ${contactInfoText}`;
    } else if (lastname) {
      title = `${lastname} - ${contactInfoText}`;
    } else {
      title = `${contactInfoText}`;
    }
    return title;
  };

  const navigateToMessagingPage = (conversationId) => {
    history.push({
      pathname: PageRoute["MESSAGING"],
      state: { conversationId }
    });
  };

  const navigateToPendingConversation = (pendingConversation) => {    
    

    history.push({
      pathname: PageRoute["MESSAGING"],
      state: { pendingConversation }
    });
  };



  const handleDirectMessageRequest = async () => {

    const memberUserId = userDetails?.userId;
    if (!memberUserId) return;

    // 1. Check for existing direct conversation
    const existingConversation = conversations?.find((c) =>
      c.members?.some((m) => m.userId === memberUserId)
    );

    if (existingConversation?.directMessageConversationId) {
      navigateToMessagingPage(existingConversation.directMessageConversationId);
      return;
    }

    // 2. Check for pending conversation
    const pending = pendingConversations?.find((c) => c.userId === memberUserId);

    if (pending) {
      navigateToPendingConversation({ userId: memberUserId });
      return;
    }

    // 3. Check messaging permission
    const messagingAllowed = userDetails?.displaySettings?.directMessage === true;

    if (messagingAllowed) {
      try {
        const res = await createConversation({
          variables: {
            userIds: [memberUserId],
          },
        });

        const created = res?.data?.directMessageContext?.createConversation;
        await addConversation(created);

        if (created?.directMessageConversationId) {
          navigateToMessagingPage(created.directMessageConversationId);
        } else {
          console.warn("Conversation created, but missing ID.");
        }
      } catch (err) {
        console.error("Failed to create direct message conversation:", err);
      }
    } else {

      const member = {
        avatarUrl: userDetails.avatarUrl,
        name: `${userDetails.firstname} ${userDetails.lastname}`.trim(),
        userId: userDetails.userId,
        username: userDetails.username,
        requireApproval: !userDetails.displaySettings?.directMessage, // Invert logic: require approval if DM is not allowed
      };

      const added = await addPendingConversation(member);
      if (added) {
        navigateToPendingConversation({ userId: member.userId });
      } else {
        console.warn("Failed to add pending conversation.");
      }
    }
  };


  return (
    <DraggableModal
      open={open}
      handleClose={handleClose}
      customClass={classes.modal}
    >
      <DraggableModalHead handleClose={handleClose} title={headerText()} />
      <DraggableModalBody customClass={classes.body}>
        {loading && (
          <Grid container justifyContent="center" className={classes.loading}>
            <CircularProgress size={30} />
          </Grid>
        )}
        {!loading && (
          <AccountSettingsReadOnly
            onDirectMessageRequest={handleDirectMessageRequest}
            userDetails={userDetails}
          />
        )}
      </DraggableModalBody>
    </DraggableModal>
  );
};

UserContactInfoModal.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  username: PropTypes.string.isRequired,
};

export default UserContactInfoModal;
